/**
 * Boot Modellen Lijst Pagina
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { bootModellenService, BootModel } from '../../services/bootModellen.service';
import ConfirmDialog from '../../components/ConfirmDialog';
import ErrorDialog from '../../components/ErrorDialog';
import { useAuth } from '../../context/AuthContext';
import './BootModellen.css';

const BootModellenList = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [bootModellen, setBootModellen] = useState<BootModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [zoek, setZoek] = useState('');
  const [debouncedZoek, setDebouncedZoek] = useState('');
  const [hasMore, setHasMore] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState<{ id: number; naam: string } | null>(null);
  const tableHeaderRef = useRef<HTMLDivElement>(null);
  const [isHeaderSticky, setIsHeaderSticky] = useState(false);
  const [tableLeft, setTableLeft] = useState(0);
  const [tableWidth, setTableWidth] = useState(0);

  // Handle search input
  const handleSearch = (value: string) => {
    setZoek(value);
  };

  // Debounce zoekterm
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedZoek(zoek);
    }, 300);
    return () => clearTimeout(timer);
  }, [zoek]);

  // Laad boot modellen
  const loadBootModellen = useCallback(async () => {
    try {
      setLoading(true);
      const data = await bootModellenService.getAll({ zoek: debouncedZoek || undefined, actief: true, limit: 50 });
      setBootModellen(data);
      setHasMore(data.length === 50);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Er is een fout opgetreden bij het ophalen van de boot modellen');
    } finally {
      setLoading(false);
    }
  }, [debouncedZoek]);

  // Laad meer boot modellen
  const handleLoadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;

    try {
      setLoadingMore(true);
      const data = await bootModellenService.getAll({
        zoek: debouncedZoek || undefined,
        actief: true,
        limit: 50,
        skip: bootModellen.length,
      });
      setBootModellen(prev => [...prev, ...data]);
      setHasMore(data.length === 50);
    } catch (err: any) {
      setError(err.message || 'Er is een fout opgetreden');
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, hasMore, bootModellen.length, debouncedZoek]);

  // Laad boot modellen wanneer zoekterm verandert
  useEffect(() => {
    loadBootModellen();
  }, [debouncedZoek]);

  // Infinite scroll
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight = document.documentElement.clientHeight;

      if (scrollHeight - scrollTop - clientHeight < 300 && hasMore && !loadingMore) {
        handleLoadMore();
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [hasMore, loadingMore, handleLoadMore]);

  // Sticky header scroll listener
  useEffect(() => {
    if (!bootModellen.length) return; // Wait for items to load

    const handleScroll = () => {
      if (tableHeaderRef.current) {
        const rect = tableHeaderRef.current.getBoundingClientRect();
        const shouldBeSticky = rect.bottom <= 185; // Show sticky when original scrolls to position 185

        // Always update position when scrolling
        setTableLeft(rect.left);
        setTableWidth(rect.width);
        setIsHeaderSticky(shouldBeSticky);
      }
    };

    // Small delay to ensure DOM is ready
    const timeout = setTimeout(() => {
      window.addEventListener('scroll', handleScroll);
      window.addEventListener('resize', handleScroll);
      handleScroll(); // Check initial position
    }, 100);

    return () => {
      clearTimeout(timeout);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [bootModellen]);

  const handleDeleteClick = (id: number, naam: string) => {
    setConfirmDelete({ id, naam });
  };

  const handleDeleteConfirm = async () => {
    if (!confirmDelete) return;

    try {
      await bootModellenService.delete(confirmDelete.id);
      setConfirmDelete(null);
      loadBootModellen();
    } catch (err: any) {
      setError(err.message || 'Er is een fout opgetreden bij het verwijderen');
      setConfirmDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setConfirmDelete(null);
  };

  if (loading && bootModellen.length === 0) {
    return <div className="loading">Laden...</div>;
  }

  return (
    <div className="boot-modellen-list">
      <div className="page-header">
        <h1>Boot Modellen</h1>
        {hasPermission('boot_modellen_aanmaken') && (
          <button
            className="btn-primary"
            onClick={() => navigate('/boot-modellen/new')}
          >
            + Nieuw Boot Model
          </button>
        )}
      </div>

      <div className="search-bar">
        <input
          type="text"
          placeholder="Zoek op fabrikant, model of type..."
          value={zoek}
          onChange={(e) => handleSearch(e.target.value)}
        />
      </div>


      {/* Vaste header rij (altijd zichtbaar) */}
      {bootModellen.length > 0 && (
        <div ref={tableHeaderRef} className="table-header" style={{
          display: 'flex',
          backgroundColor: '#f8f9fa',
          borderBottom: '2px solid #dee2e6',
          padding: '0.75rem 1rem',
          marginTop: '1rem',
          borderRadius: '8px 8px 0 0',
          fontWeight: 600,
          fontSize: '0.875rem',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: '#495057'
        }}>
          <div style={{ flex: '1.2', minWidth: 0, paddingRight: '1rem' }}>Fabrikant</div>
          <div style={{ flex: '1.2', minWidth: 0, paddingRight: '1rem' }}>Model</div>
          <div style={{ flex: '0.8', minWidth: 0, paddingRight: '1rem' }}>Type</div>
          <div style={{ flex: '0.6', minWidth: 0, paddingRight: '1rem' }}>Lengte (m)</div>
          <div style={{ flex: '0.6', minWidth: 0, paddingRight: '1rem' }}>Breedte (m)</div>
          <div style={{ flex: '1', minWidth: 0, paddingRight: '1rem' }}>Bouwjaren</div>
          <div style={{ flex: '1.5', minWidth: 0 }}>Acties</div>
        </div>
      )}

      {/* Sticky header overlay (duplicaat) */}
      {isHeaderSticky && (
        <div className="table-header" style={{
          position: 'fixed',
          top: '185px',
          left: `${tableLeft}px`,
          width: `${tableWidth}px`,
          zIndex: 1000,
          display: 'flex',
          backgroundColor: '#f8f9fa',
          borderBottom: '2px solid #dee2e6',
          borderRadius: '8px 8px 0 0',
          padding: '0.75rem 1rem',
          fontWeight: 600,
          fontSize: '0.875rem',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: '#495057'
        }}>
          <div style={{ flex: '1.2', minWidth: 0, paddingRight: '1rem' }}>Fabrikant</div>
          <div style={{ flex: '1.2', minWidth: 0, paddingRight: '1rem' }}>Model</div>
          <div style={{ flex: '0.8', minWidth: 0, paddingRight: '1rem' }}>Type</div>
          <div style={{ flex: '0.6', minWidth: 0, paddingRight: '1rem' }}>Lengte (m)</div>
          <div style={{ flex: '0.6', minWidth: 0, paddingRight: '1rem' }}>Breedte (m)</div>
          <div style={{ flex: '1', minWidth: 0, paddingRight: '1rem' }}>Bouwjaren</div>
          <div style={{ flex: '1.5', minWidth: 0 }}>Acties</div>
        </div>
      )}

      {/* Boot modellen lijst - Desktop */}
      {bootModellen.length > 0 && (
        <div className="desktop-table" style={{
          background: 'white',
          borderRadius: '0 0 8px 8px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          overflow: 'hidden'
        }}>
          {bootModellen.map((model) => (
            <div key={model.id} style={{
              display: 'flex',
              alignItems: 'center',
              padding: '0.75rem 1rem',
              borderBottom: '1px solid #dee2e6',
              transition: 'background 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#f8f9fa'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
            >
              <div style={{ flex: '1.2', minWidth: 0, fontWeight: 500, paddingRight: '1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{model.fabrikant}</div>
              <div style={{ flex: '1.2', minWidth: 0, fontWeight: 500, paddingRight: '1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{model.model}</div>
              <div style={{ flex: '0.8', minWidth: 0, paddingRight: '1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{model.type_boot || '-'}</div>
              <div style={{ flex: '0.6', minWidth: 0, paddingRight: '1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{model.lengte ? Number(model.lengte).toFixed(2) : '-'}</div>
              <div style={{ flex: '0.6', minWidth: 0, paddingRight: '1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{model.breedte ? Number(model.breedte).toFixed(2) : '-'}</div>
              <div style={{ flex: '1', minWidth: 0, paddingRight: '1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {model.bouwjaar_vanaf || model.bouwjaar_tot
                  ? `${model.bouwjaar_vanaf || '?'} - ${model.bouwjaar_tot || 'heden'}`
                  : '-'}
              </div>
              <div className="actions" style={{ flex: '1.5', minWidth: 0 }}>
                {hasPermission('boot_modellen_bewerken') && (
                  <button
                    className="btn-small btn-secondary"
                    onClick={() => navigate(`/boot-modellen/${model.id}`)}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    Bewerken
                  </button>
                )}
                {hasPermission('boot_modellen_verwijderen') && (
                  <button
                    className="btn-small btn-danger"
                    onClick={() => handleDeleteClick(model.id, `${model.fabrikant} ${model.model}`)}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    Verwijderen
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Boot modellen lijst - Mobile tegels */}
      {bootModellen.length > 0 && (
        <div className="mobile-cards">
          {bootModellen.map((model) => (
            <div key={model.id} className="boot-card">
              <div className="boot-card-header">
                <span className="boot-name">{model.fabrikant} {model.model}</span>
                {model.type_boot && <span className="boot-type">{model.type_boot}</span>}
              </div>
              <div className="boot-card-body">
                {(model.lengte || model.breedte) && (
                  <div className="boot-info">
                    <span className="label">Afmetingen:</span> {model.lengte ? Number(model.lengte).toFixed(2) : '-'} x {model.breedte ? Number(model.breedte).toFixed(2) : '-'} m
                  </div>
                )}
                {(model.bouwjaar_vanaf || model.bouwjaar_tot) && (
                  <div className="boot-info">
                    <span className="label">Bouwjaren:</span> {model.bouwjaar_vanaf || '?'} - {model.bouwjaar_tot || 'heden'}
                  </div>
                )}
              </div>
              <div className="boot-card-actions">
                {hasPermission('boot_modellen_bewerken') && (
                  <button
                    className="btn-small btn-secondary"
                    onClick={() => navigate(`/boot-modellen/${model.id}`)}
                  >
                    Bewerken
                  </button>
                )}
                {hasPermission('boot_modellen_verwijderen') && (
                  <button
                    className="btn-small btn-danger"
                    onClick={() => handleDeleteClick(model.id, `${model.fabrikant} ${model.model}`)}
                  >
                    Verwijderen
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {bootModellen.length === 0 && !loading && (
        <div style={{
          padding: '3rem',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          textAlign: 'center',
          marginTop: '1rem'
        }}>
          Geen boot modellen gevonden
        </div>
      )}

      {loadingMore && (
        <div style={{ textAlign: 'center', padding: '1rem', color: '#666' }}>
          Meer laden...
        </div>
      )}

      {!hasMore && bootModellen.length > 0 && (
        <div style={{ textAlign: 'center', padding: '1rem', color: '#999' }}>
          Alle boot modellen geladen ({bootModellen.length} {bootModellen.length === 1 ? 'model' : 'modellen'})
        </div>
      )}

      <ConfirmDialog
        isOpen={!!confirmDelete}
        title="Boot Model Verwijderen"
        message={`Weet je zeker dat je "${confirmDelete?.naam}" wilt verwijderen? Dit model wordt gedeactiveerd en verdwijnt uit de lijst.`}
        confirmText="Verwijderen"
        cancelText="Annuleren"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        danger={true}
      />

      <ErrorDialog
        isOpen={!!error}
        message={error || ''}
        onClose={() => setError(null)}
      />
    </div>
  );
};

export default BootModellenList;
